import { useState, useReducer, useCallback, useEffect, useMemo, useRef } from 'react';
import { AIModelSource } from '@bingads-webui-campaign-react/asset-library-model';
import { detectBrandName } from './util';

export const statuses = {
  entry: 'entry',
  finished: 'finished',
  loading: 'loading',
  cancelling: 'cancelling',
};

export const actions = {
  search: 'search',
  finish: 'finish',
  cancel: 'cancel',
  fail: 'fail',
};

const reducer = (state, action) => {
  // action, [search, finish, cancel, failed]
  switch (state.status) {
    case statuses.entry:
    case statuses.finished:
      switch (action.type) {
        case actions.search:
          return ({ status: statuses.loading });
        default:
          break;
      }
      return ({ status: statuses.finished });
    case statuses.loading:
      switch (action.type) {
        case actions.cancel:
        case actions.fail:
          return ({ status: statuses.cancelling });
        case actions.finish:
          return ({ status: statuses.finished });
        case actions.search:
          return ({ status: statuses.loading });
        default:
          break;
      }
      return ({ status: statuses.finished });
    case statuses.cancelling:
      switch (action.type) {
        case actions.finish:
        case actions.fail:
          return ({ status: statuses.finished });
        default:
          break;
      }
      return ({ status: statuses.finished });
    default:
      return ({ status: statuses.finished });
  }
};

export const useAIGenerateImagesState = ({
  dataService, i18n, logger, eventHandler,
}) => {
  const log = useCallback((...args) => {
    if (logger) {
      logger.logTrace(...args);
    }
  }, [logger]);

  const [prompt, setPrompt] = useState(undefined);
  const [state, dispatch] = useReducer(reducer, { status: statuses.entry });
  const [images, setImages] = useState([]);
  const [serverError, setServerError] = useState(undefined);
  const [detectedBrandName, setDetectedBrandName] = useState(null);

  const onSelectImage = useCallback((index) => {
    const newImages = [...images];
    newImages[index].isSelected = !newImages[index].isSelected;

    log('AIGenerateImagesView: select image');
    setImages(newImages);
  }, [setImages, images, log]);

  const selectedImages = useMemo(() => images.filter(x => x.isSelected), [images]);

  const generateImagesPromise = useRef(undefined);

  const getGenerateImagesByAIPromise = useCallback((skipBrandDetection) => {
    setImages([]);
    let cancelled = false;
    let statusPoller = null;

    const onStartPolling = (poller) => {
      statusPoller = poller;
    };

    const aigcCallStart = Date.now();

    dataService.generateImagesByAI({
      prompt, onStartPolling /* , onProgress */, skipBrandDetection,
    }).then(({
      images: newImages, brandNameDetected, brandNames,
    }) => {
      const aigcCallEnd = Date.now();
      const aigcCallDuration = aigcCallEnd - aigcCallStart;
      const hasRecraftImage = newImages.some(({ modelSource }) => modelSource === AIModelSource.Recraft);

      // use the tag "logForExpand" to expand the log to per image Url without expanding other logs with imageUrls
      log('AIGenerateImagesView: count AI generated images', {
        imageUrls: newImages.map(({ url }) => url).join(','),
        logForExpand: true,
        skipBrandDetection,
        prompt,
        brandNameDetected: !!brandNameDetected,
        hasRecraftImage,
        modelSources: newImages.map(({ modelSource }) => modelSource).join(','),
        duration: aigcCallDuration,
      }); // will expand the log to per image Url
      if (eventHandler && eventHandler.onReturnImages) {
        eventHandler.onReturnImages({ imageIds: newImages.map(({ id }) => id) });
      }

      if (!cancelled) {
        if (brandNameDetected) {
          setDetectedBrandName(brandNames);
          log(`AIGenerateImagesView: detected brand names: ${brandNames}, prompt: ${prompt}`);
        } else {
          setImages(newImages.map(({ url, modelSource }) => ({ thumbnailSrc: url, modelSource })));
          dispatch({ type: actions.finish });
        }
      }
    }).catch((error) => {
      if (!cancelled) {
        dispatch({ type: actions.fail });
        if (error && error.uxMessage) {
          setServerError(i18n.getString(error.uxMessage));
        } else {
          setServerError(i18n.getString(_TL_('We\'ve encountered an error and failed to generate images.')));
        }
      }
    });

    return ({
      cancel: () => {
        cancelled = true;
        if (statusPoller && statusPoller.stop) {
          statusPoller.stop();
          statusPoller = null;
        }
      },
    });
  }, [dataService, prompt, i18n, log, eventHandler]);

  const getImages = useCallback((skipBrandDetection = false) => {
    dispatch({ type: actions.search });
    setServerError(undefined);
    log('AIGenerateImagesView: generate image', { prompt });
    if (eventHandler && eventHandler.onGenerateImages) {
      eventHandler.onGenerateImages({ prompt });
    }
    generateImagesPromise.current = getGenerateImagesByAIPromise(skipBrandDetection);
  }, [log, prompt, getGenerateImagesByAIPromise, eventHandler]);

  const cancel = useCallback(() => {
    dispatch({ type: actions.cancel });
    log('AIGenerateImagesView: cancel');
    if (generateImagesPromise.current) {
      generateImagesPromise.current.cancel();
      generateImagesPromise.current = null;
    }
  }, [log]);

  useEffect(() => {
    if (state.status === statuses.cancelling) {
      dispatch({ type: actions.finish });
    }
  }, [state.status]);

  useEffect(() => {
    if (serverError != null) {
      setServerError(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt]);

  const isValid = useMemo(() => {
    let containsBrand = false;
    if (detectedBrandName) {
      // multiple brand names are separated by comma
      // replace special characters in brand names and split by comma
      containsBrand = detectBrandName(detectedBrandName, prompt);
    }
    const trimmedPrompt = prompt ? prompt.trim() : '';
    const isValidPrompt = prompt != null && prompt.length > 0 && trimmedPrompt.length > 0 && prompt.length <= 256;
    return isValidPrompt && !containsBrand;
  }, [detectedBrandName, prompt]);

  const promptError = useMemo(() => {
    if (prompt == null) {
      return undefined;
    }
    if (prompt.length === 0) {
      return i18n.getString(_TL_('Prompt is required'));
    }
    if (prompt.length > 256) {
      return i18n.getString(_TL_('Prompt must be less than 256 characters'));
    }
    return undefined;
  }, [prompt, i18n]);

  return {
    images,
    selectedImages,
    isValid,
    getImages,
    onSelectImage,
    prompt,
    promptError,
    setPrompt,
    state,
    cancel,
    serverError,
    detectedBrandName,
    setDetectedBrandName,
  };
};
